/**
 * The race behind the title: two ninjas, pink and cyan headbands, running the same endless course
 * across the night sky under the menus, the way an arcade cabinet plays itself between games. The
 * course and the runners are `autopilot.ts`; this draws them on a stage of their own through the
 * game's own world renderer (the same neon the courses wear), the leader kept left of the middle
 * where the menu panel does not cover it. Nothing here is game state.
 */

import {
  NEON,
  type Pose,
  type PoseName,
  type RenderFeel,
  SilhouetteRenderer,
  ViewState,
  WorldRenderer,
} from "@lache/render";
import type { Tuning } from "@lache/sim";
import { type RaceRunner, RaceSim } from "./autopilot";
import { MiniStage } from "./stage";

/** Meters across the longer side of the screen. */
const VIEW_WIDTH = 26;
/** The ground line this far up the screen. */
const GROUND_FRACTION = 0.14;
/** The leader this far from the left edge on a wide screen (the menu panel takes the middle). */
const LEADER_FRACTION = 0.3;
/** The camera's half-life toward its target, s. */
const CAMERA_HALF_LIFE = 0.18;

interface Drawn {
  runner: RaceRunner;
  view: ViewState;
  body: SilhouetteRenderer | null;
}

export class AttractRace {
  private readonly stage: MiniStage;
  private readonly race: RaceSim;
  private readonly drawn: Drawn[];
  private world: WorldRenderer | null = null;
  private cameraX = 0;
  private cameraY = 0;
  private cameraSet = false;
  private visible = false;
  private started = false;

  constructor(
    parent: HTMLElement,
    tuning: Tuning,
    private readonly feel: RenderFeel,
    private readonly poses: Record<PoseName, Pose>,
  ) {
    this.race = new RaceSim(tuning);
    this.drawn = this.race.runners.map((runner) => ({ runner, view: new ViewState(), body: null }));
    // Half resolution: a backdrop is soft anyway, and it keeps software GL (the tests, a weak phone) quick.
    this.stage = new MiniStage({
      parent,
      className: "attract-canvas",
      resizeTo: document.body,
      resolution: 0.5,
    });
  }

  private build(): void {
    const vp = this.stage.viewport;
    this.world = new WorldRenderer(this.stage.shapes, this.race.world, this.feel, { lines: false });
    vp.world.addChild(this.world.root);
    const accents = [NEON.pink, NEON.cyan];
    this.drawn.forEach((d, i) => {
      const body = new SilhouetteRenderer(this.stage.shapes, this.poses);
      body.setAccent(accents[i]!);
      vp.world.addChild(body.root);
      d.body = body;
    });
  }

  /** Tick and draw the race when the backdrop shows; the clock rests while it is hidden. */
  update(visible: boolean, nowMs: number): void {
    if (!visible) {
      if (this.visible) this.stage.resetClock();
      this.visible = false;
      return;
    }
    this.visible = true;
    if (!this.started) {
      this.started = true;
      void this.stage.init().then(() => this.build());
    }
    if (!this.stage.ready) return;
    const { ticks, alpha, dt } = this.stage.clock(nowMs);
    for (let i = 0; i < ticks; i++) if (this.race.step()) this.cameraX -= this.race.period;
    this.stage.fit(VIEW_WIDTH, this.feel);
    const vp = this.stage.viewport;
    const cam = vp.camera;
    let leader = this.drawn[0]!;
    for (const d of this.drawn) {
      const sim = d.runner.sim;
      d.view.update(sim.prev, sim.state, alpha, sim.tuning, this.feel, dt);
      if (d.view.x > leader.view.x) leader = d;
    }
    // The leader sits left of the middle on a wide screen, so the menu panel covers the course ahead
    // rather than the runners; the ground line stays put near the bottom.
    const wide = this.stage.width >= this.stage.height;
    const across = this.stage.width / cam.ppm;
    const targetX = leader.view.x + (0.5 - (wide ? LEADER_FRACTION : 0.42)) * across;
    const targetY = (0.5 - GROUND_FRACTION) * (this.stage.height / cam.ppm);
    if (!this.cameraSet) {
      this.cameraX = targetX;
      this.cameraSet = true;
    } else {
      const k = 1 - 0.5 ** (dt / CAMERA_HALF_LIFE);
      this.cameraX += (targetX - this.cameraX) * k;
    }
    this.cameraY = targetY;
    cam.snapTo(this.cameraX, this.cameraY);
    vp.applyCamera();
    this.world?.update(this.feel, nowMs);
    for (const d of this.drawn) d.body?.draw(d.view, d.runner.sim, this.feel, dt);
    this.stage.render();
  }
}
